import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import './index.scss';

export interface Option {
  label?: string;
  value?: string | number;
  id?: string | number;
  name?: string;
  disabled?: boolean;
}

export interface DropdownProps {
  id?: string;
  values?: any[];
  value?: any;
  options?: Option[];
  allOptions?: Option[];
  label?: string;
  required?: boolean;
  name?: string;
  multiSelect?: boolean;
  multi?: boolean;
  placeholder?: string;
  rootClassName?: string;
  inputClass?: string;
  disabled?: boolean;
  noSearch?: boolean;
  hideClearIcon?: boolean;
  disableClearable?: boolean;
  menuPlacement?: 'down' | 'up';
  compact?: boolean;
  error?: boolean | string;
  errorMessage?: string;
  searchText?: string;
  onChange?: (_: any, name?: string) => void;
  onValuesChange?: (_: string[]) => void;
  onBlur?: (_?: any, name?: string) => void;
  onSearch?: (_: any, name?: string) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  values: propValues,
  value: propValue,
  options: rawOptions = [],
  allOptions = [],
  label,
  required,
  name = '',
  multiSelect,
  multi,
  placeholder,
  rootClassName = '',
  inputClass = '',
  disabled,
  noSearch,
  hideClearIcon,
  disableClearable,
  menuPlacement = 'down',
  compact,
  error,
  errorMessage,
  searchText: propSearchText,
  onChange,
  onValuesChange,
  onBlur,
  onSearch,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number; width: number }>({
    left: 0,
    width: 0,
  });

  const isMulti = Boolean(multiSelect || multi);

  const updateCoords = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeUp = menuPlacement === 'up' || (spaceBelow < 250 && rect.top > 250);
    const width = Math.max(rect.width, compact ? 120 : 160);

    setCoords({
      left: rect.left,
      width,
      top: placeUp ? undefined : rect.bottom + 4,
      bottom: placeUp ? window.innerHeight - rect.top + 4 : undefined,
    });
  }, [menuPlacement, compact]);

  const optionsList = useMemo(() => {
    const list = rawOptions.length > 0 ? rawOptions : allOptions;
    return list.map((opt) => ({
      id: String(opt.id ?? opt.value ?? ''),
      name: String(opt.name ?? opt.label ?? opt.id ?? opt.value ?? ''),
      disabled: Boolean(opt.disabled),
    }));
  }, [rawOptions, allOptions]);

  const selectedValues = useMemo(() => {
    if (Array.isArray(propValues)) return propValues.map(String);
    if (propValue !== undefined && propValue !== null && propValue !== '') return [String(propValue)];
    return [];
  }, [propValues, propValue]);

  useEffect(() => {
    if (!open) return;
    updateCoords();

    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur?.(undefined, name);
      }
    };

    const handleScrollOrResize = () => {
      updateCoords();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, onBlur, name, updateCoords]);

  const handleToggle = (rawId: string) => {
    const targetId = String(rawId);
    let nextValues: string[];
    if (isMulti) {
      nextValues = selectedValues.includes(targetId)
        ? selectedValues.filter((v) => v !== targetId)
        : [...selectedValues, targetId];
    } else {
      nextValues = [targetId];
      setOpen(false);
    }

    if (onValuesChange) {
      onValuesChange(nextValues);
    }
    if (onChange) {
      const valArg = isMulti ? nextValues : nextValues[0] ?? '';
      onChange({ target: { name, value: valArg } }, name);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onValuesChange) onValuesChange([]);
    if (onChange) onChange({ target: { name, value: isMulti ? [] : '' } }, name);
  };

  const filteredOptions = useMemo(() => {
    if (!query) return optionsList;
    return optionsList.filter((opt) => opt.name.toLowerCase().includes(query.toLowerCase()));
  }, [optionsList, query]);

  const selectedLabels = useMemo(() => {
    return selectedValues.map((v) => {
      const found = optionsList.find((opt) => opt.id === v);
      return found ? found.name : v;
    });
  }, [selectedValues, optionsList]);

  const hasError = Boolean(error);
  const displayErr = typeof error === 'string' ? error : errorMessage;

  return (
    <div className={`d-dropdown ${rootClassName}`} ref={rootRef}>
      {label && (
        <label className="mb-[9px] block font-sans text-[10px] uppercase tracking-[0.16em] text-(--dim)" style={{ marginBottom: '6px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--dim)', fontFamily: 'var(--font-sans)', display: 'block' }}>
          {label}
          {required && <span className="text-danger ms-1" style={{ color: 'var(--red)' }}>*</span>}
        </label>
      )}

      <div className="position-relative w-100">
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          className={clsx('dropdown-select-input', open && 'dropdown-active', hasError && 'dropdown-is-invalid', disabled && 'dropdown-disabled', inputClass)}
          style={{
            height: compact ? '32px' : '42px',
            minHeight: compact ? '32px' : '42px',
            padding: compact ? '5px 10px' : '8px 12px',
            fontSize: compact ? '13px' : '14.5px',
            lineHeight: '1.2',
            width: '100%',
            minWidth: compact ? '120px' : undefined,
            borderRadius: 'var(--radius-ctl, 12px)',
            border: '1px solid var(--line2)',
            backgroundColor: disabled ? 'var(--surf2)' : 'var(--surf)',
            color: 'var(--txt)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            textAlign: 'left',
            fontFamily: 'var(--font-sans)',
            boxSizing: 'border-box',
          }}
        >
          {isMulti && selectedValues.length > 0 ? (
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
              {selectedLabels.map((lbl, idx) => (
                <span
                  key={idx}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--acc-dim)',
                    padding: '3px 8px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: 'var(--acc-text)',
                  }}
                >
                  {lbl}
                  {!disabled && (
                    <span
                      role="button"
                      tabIndex={-1}
                      style={{ cursor: 'pointer', color: 'var(--red)', opacity: 0.8 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(selectedValues[idx]);
                      }}
                    >
                      ✕
                    </span>
                  )}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-truncate" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selectedValues.length === 0 ? 'var(--placeholder)' : 'var(--txt)' }}>
              {selectedValues.length > 0
                ? selectedLabels.join(', ')
                : (placeholder ?? t('common.dropdown.placeholder') ?? 'Select...')}
            </span>
          )}

          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {selectedValues.length > 0 && !disabled && !hideClearIcon && !disableClearable && !compact && (
              <span role="button" tabIndex={-1} style={{ color: 'var(--red)', cursor: 'pointer' }} onClick={handleClear}>
                ✕
              </span>
            )}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                color: 'var(--faint)',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s ease',
              }}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>

        {open &&
          createPortal(
            <div
              className="dropdown-menu-popover-root"
              style={{
                position: 'fixed',
                left: `${coords.left}px`,
                width: `${coords.width}px`,
                top: coords.top !== undefined ? `${coords.top}px` : 'auto',
                bottom: coords.bottom !== undefined ? `${coords.bottom}px` : 'auto',
                zIndex: 9999,
                maxHeight: '280px',
                overflow: 'hidden',
                borderRadius: 'var(--radius-ctl, 12px)',
                border: '1px solid var(--line2)',
                backgroundColor: 'var(--surf3, #15181b)',
                boxShadow: 'var(--shadow)',
                padding: '6px',
                boxSizing: 'border-box',
              }}
            >
              {(!noSearch || onSearch) && (
                <div style={{ padding: '4px 6px 8px 6px', borderBottom: '1px solid var(--line2)' }}>
                  <input
                    autoFocus
                    type="search"
                    value={propSearchText ?? query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      onSearch?.(e.target.value, name);
                    }}
                    placeholder={t('common.dropdown.search') ?? 'Search...'}
                    style={{
                      width: '100%',
                      border: '1px solid var(--line2)',
                      borderRadius: '8px',
                      backgroundColor: 'var(--surf2)',
                      padding: '6px 10px',
                      fontSize: '13px',
                      color: 'var(--txt)',
                      outline: 'none',
                      fontFamily: 'var(--font-sans)',
                    }}
                  />
                </div>
              )}

              <div style={{ maxHeight: '220px', overflowY: 'auto', padding: '4px 0' }}>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--faint)', fontFamily: 'var(--font-sans)' }}>
                    {t('common.dropdown.noRecordsFound') ?? 'No options'}
                  </div>
                ) : (
                  filteredOptions.map((opt) => {
                    const isSelected = selectedValues.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        disabled={opt.disabled}
                        onClick={() => !opt.disabled && handleToggle(opt.id)}
                        style={{
                          display: 'flex',
                          width: '100%',
                          alignItems: 'center',
                          gap: '10px',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontSize: '13.5px',
                          fontFamily: 'var(--font-sans)',
                          border: 'none',
                          backgroundColor: isSelected ? 'var(--surf2)' : 'transparent',
                          color: isSelected ? 'var(--acc-text)' : 'var(--txt)',
                          fontWeight: isSelected ? 600 : 400,
                          cursor: opt.disabled ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.15s ease, color 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!opt.disabled) e.currentTarget.style.backgroundColor = 'var(--surf)';
                        }}
                        onMouseLeave={(e) => {
                          if (!opt.disabled) e.currentTarget.style.backgroundColor = isSelected ? 'var(--surf2)' : 'transparent';
                        }}
                      >
                        {isMulti && (
                          <span
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '4px',
                              border: isSelected ? '1px solid var(--acc)' : '1px solid var(--faint)',
                              backgroundColor: isSelected ? 'var(--acc)' : 'transparent',
                              color: 'var(--acc-ink)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {isSelected && '✓'}
                          </span>
                        )}
                        <span>{opt.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {hasError && displayErr && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--red)', fontFamily: 'var(--font-sans)' }}>
          {displayErr}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
